import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { startOfMonth, subMonths } from "date-fns";
import moment from "moment";

import { stateDate } from "../../../../utils/formatDate";
import { createMultiOptions } from '../../../../utils/createOptions';
import { IVacancyListOptions } from '../../../../types/vacancyInterfeces';
import { Item, ItemEmployeeOptions } from '../../../../types/interfaces';

const initialState: any = {
    limit: JSON.parse(localStorage.getItem('pageLimit') as string) || 10,
    data: null,
    pages: 1,
    page: 1,
    applicationData: {
        firstName: '',
        lastName: '',
        gender: 'Male',
    },
    vacancyListInputValues: {
        cities: [],
        area: [],
        project: [],
        regions: [],
        status: ['INTERN_NOT_DIRECT', 'REPLACEMENT', 'TERRITORY_OPEN', 'VACATION', 'UNPROFITABLE', 'LINGERING_VACANT'],
        supervisors: [],
        type: null,
    },
    modifiedDate: {
        formattedDateFrom: moment(
            stateDate(startOfMonth(subMonths(new Date(), 1)))
        ).format("DD/MM/YYYY"),
        formattedDateTo: moment(stateDate(new Date())).format("DD/MM/YYYY"),
    },
    createDate: {
        formattedDateFrom: moment(
            stateDate(startOfMonth(subMonths(new Date(), 1)))
        ).format("DD/MM/YYYY"),
        formattedDateTo: moment(stateDate(new Date())).format("DD/MM/YYYY"),
    },
    vacancyListSettings: JSON.parse(
        localStorage.getItem('selectedVacancyListTabs') as string
    ) || [
            'vacancyId',
            'type',
            'regions',
            'areas',
            'cities',
            'subway',
            'addresses',
            'merchs',
            'vacancyName',
            'status',
            'projectName',
            'routeNo',
            'vsv',
            'sv',
            'schedule',
            'salary',
            'revenue',
            'razm',
            'createdDate',
            'modifiedDate',
        ],
    isPending: true,
    filters: {
        areas: [],
        projects: [],
        region: [],
        statuses: [],
        supervisors: [],
        types: [],
        schedules: []
    },
};

export const vacancyListSlice = createSlice({
    name: 'vacancyList',
    initialState,
    reducers: {
        reducerGetVacancyList: (state: Draft<any>, action: PayloadAction<any>) => {
            if (action.payload?.data?.data?.length) {
                state.isPending = false
            }
            if (action.payload.data.count > 0 && action.payload.data.data) {
                state.data = action.payload.data.data.map((item: any) => {
                    const isChanged = Object.keys(item).some((key) => {
                        return item[key].changed === true;
                    });
                    const newArrValue = (value: any, valueExt: any) => {
                        const combinedAreas = [...(value || []), ...(valueExt || [])];
                        const uniqueAreas = combinedAreas.reduce((acc, el) => {
                            const existingIndex = el.employee ? acc.findIndex((a: ItemEmployeeOptions) => a.employee.id === el.employee.id) : acc.findIndex((a: Item) => a.id === el.id);
                            if (existingIndex === -1) {
                                acc.push(el);
                            }
                            return acc;
                        }, []);
                        return uniqueAreas;
                    }
                    const newStringValue = (item: any, name: string) => {
                        if (item[name].value && item[name].ext) {
                            const allValues = [...item[name].value, ...item[name].ext.split(',')];
                            const uniqueValues: string[] = Array.from(new Set(allValues));
                            return uniqueValues;
                        } else {
                            return (item[name].value || item[name].ext) ? (item[name].value || item[name].ext.split(",").map((val: string) => val.trim())) : null;
                        }
                    }

                    item.areas.valueAll = newArrValue(item.areas.value || [], item.areas.ext || []);
                    item.regions.valueAll = newArrValue(item.regions.value || [], item.regions.ext || []);
                    item.virtualSupervisors.valueAll = newArrValue(item.virtualSupervisors.value || [], item.virtualSupervisors.ext || []);
                    item.supervisors.valueAll = newArrValue(item.supervisors.value || [], item.supervisors.ext || []);
                    item.cities.valueAll = newStringValue(item, 'cities');
                    item.merchs.valueAll = newStringValue(item, 'merchs');
                    item.routes.valueAll = newStringValue(item, 'routes');

                    if (item.addresses.value && item.addresses.ext) {
                        const arr: any = [];
                        item?.addresses?.value?.map((el: string) => {
                            if (!item.addresses?.ext?.includes(el)) {
                                arr.push(el)
                            }
                            return el;
                        })
                        const uniqueCities = arr.concat(item.addresses.ext);
                        item.addresses.valueAll = uniqueCities;
                    } else {
                        item.addresses.valueAll = (item.addresses.value || item.addresses.ext) ? item.addresses.value || [item.addresses.ext] : null;
                    }

                    if (item.schedule.value && item.schedule.ext) {
                        if (Object.keys(item.schedule.value)[0] === Object.keys(item.schedule.ext)[0]) {
                            item.schedule.valueAll = [item.schedule.value];
                        } else {
                            item.schedule.valueAll = [item.schedule.value, item.schedule.ext];
                        }
                    } else {
                        item.schedule.valueAll = item.schedule.value || item.schedule.ext ? [item.schedule.value || item.schedule.ext] : null;
                    }

                    return {
                        ...item,
                        isChanged: isChanged
                    };
                });
            } else {
                state.data = [];
            }

            state.count = action.payload.data.count;
            state.checkboxes = action.payload.data?.data?.reduce(
                (acc: boolean[], vacancy: IVacancyListOptions) => {
                    const vacancyId = vacancy.vacancy.value.id;
                    acc[vacancyId] = false;
                    return acc;
                },
                {}
            );
        },
        reducerGetProject: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.vacancyListInputValues.project = action.payload;
        },
        reducerSetVacancyPending: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.isPending = action.payload;
        },
        reducerSelectedVacanciesId: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.selectedVacanciesId = action.payload;
        },
        reducerSelectedVacancies: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.selectedVacancy = action.payload;
        },
        reducerVacancyStatus: (state: Draft<any>, action: PayloadAction<any>) => {
            state.clientsStatus = action.payload;
        },
        reducerVacancyStatusMessage: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.clientsStatusMessage = action.payload;
        },
        reducerApplicationChange: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.applicationData = action.payload;
        },
        reducerVacancyListSettings: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.vacancyListSettings = action.payload;
        },
        reducerVacancyListInputValues: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.vacancyListInputValues = action.payload;
            if (action.payload.modifiedDate) {
                state.modifiedDate.formattedDateFrom = moment(action.payload.modifiedDate?.from).format(
                    "DD/MM/YYYY"
                );
                state.modifiedDate.formattedDateTo = moment(action.payload.modifiedDate?.to).format(
                    "DD/MM/YYYY"
                );
            }
            if (action.payload.createDate) {
                state.createDate.formattedDateFrom = moment(action.payload.createDate?.from).format(
                    "DD/MM/YYYY"
                );
                state.createDate.formattedDateTo = moment(action.payload.createDate?.to).format(
                    "DD/MM/YYYY"
                );
            }
        },
        reducerVacancyFiltersList: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.filters.areas = action.payload.data.areas?.map(
                (option: { id: number; name: string; region: { id: number; } }) => ({
                    value: option,
                    label: option.name,
                })
            );
            const createSelectOptions = (data: { [key: string]: string }[]) => {
                if (!data) return [];
                const options = data.map((el: { [key: string]: string }) => ({
                    value: Object.keys(el).toString(),
                    label: Object.values(el).toString()
                }));
                return options;
            };
            state.filters.schedules = createSelectOptions(action.payload.data.schedules);
            state.filters.statuses = createSelectOptions(action.payload.data.statuses);
            state.filters.types = createSelectOptions(action.payload.data.types);
            state.filters.regions = createMultiOptions(action.payload.data.reigons);
            state.filters.projects = createMultiOptions(action.payload.data.projects);
            state.filters.supervisors = createMultiOptions(action.payload.data.supervisors);
            state.filters.virtualSupervisors = createMultiOptions(action.payload.data.virtualSupervisors);
        },
        reducerVacancyMultiselect: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.vacancyListInputValues = { ...state.vacancyListInputValues, ...action.payload };
        },
        reducerVacancyClearInputValues: () => {
            return initialState;
        },
    },
});

export const {
    reducerGetVacancyList,
    reducerSelectedVacancies,
    reducerSelectedVacanciesId,
    reducerVacancyStatus,
    reducerVacancyStatusMessage,
    reducerApplicationChange,
    reducerVacancyListSettings,
    reducerVacancyListInputValues,
    reducerSetVacancyPending,
    reducerVacancyFiltersList,
    reducerVacancyMultiselect,
    reducerGetProject,
    reducerVacancyClearInputValues,
} = vacancyListSlice.actions;

export default vacancyListSlice.reducer;
